import useViewParamsProps from '@/composables/view-params/view-params-props';
import { AND } from 'shared/api/query/filter';
import { CARD_SIZE_LARGE, CARD_SIZE_MEDIUM, CARD_SIZE_SMALL } from '@/lib/constants';
import { DateTime } from 'luxon';
import { GoalFilterHandler } from '@/lib/filter/goal/handler';
import { compareToDateTime, showCompareTo as libShowCompareTo } from '@/lib/compare-to';
import { computed } from 'vue';
import { goal as goalConfig } from 'shared/api/query/configs.json';
import { viewType as viewTypes } from 'shared/constants.json';

export default function useCascadeViewParams(viewsService, defaultFilter, loggedInUserAccount, searchTerm, selectedCycles) {
  const queryFilter = computed(() => {
    if (defaultFilter === null) {
      return viewsService.currentView.value.params.filter;
    }
    if (viewsService.currentView.value.params.filter === null) {
      return {
        account: { uid: loggedInUserAccount.value.uid },
        op: AND,
        children: [defaultFilter],
      };
    }
    return {
      account: { uid: loggedInUserAccount.value.uid },
      op: AND,
      children: [defaultFilter, ...viewsService.currentView.value.params.filter.children],
    };
  });

  const gqlFilterObject = computed(() => {
    const handler = new GoalFilterHandler();
    return handler.Translate(queryFilter.value);
  });

  const applyFilterOnFirstLevelOnly = computed(() => viewsService.currentView.value.params.applyFilterOnFirstLevelOnly);

  const order = computed(() => {
    if (viewsService.currentView.value.params === undefined || viewsService.currentView.value.params.order === undefined) {
      return [];
    }
    return viewsService.currentView.value.params.order;
  });

  const showCompareTo = computed(() => libShowCompareTo(viewsService.currentView.value.params.compare));
  const compareToNow = DateTime.local();
  const compareTo = computed(() => {
    const compareTo = compareToDateTime(viewsService.currentView.value.params.compare, compareToNow);
    if (compareTo === null) {
      return [];
    }
    return [compareTo.toISO()];
  });

  const showParents = computed(() => {
    // TODO: Only cascade has ever been allowed to showParents, e.g. in tree parent alignment was not a visible prop.
    //  We might want to show parents in tree if we do work towards showing longer parent chains in different views, and create "disabled" cards
    if ([viewTypes.cascade].includes(viewsService.currentView.value.viewType)) {
      return viewsService.currentView.value.params.showParents === true;
    }
    return false;
  });

  const cardWidth = computed(() => {
    switch (viewsService.currentView.value.params.cardWidth) {
      case CARD_SIZE_SMALL:
        return 200;
      case CARD_SIZE_MEDIUM:
        return 260;
      case CARD_SIZE_LARGE:
        return 320;
      default:
        return 260;
    }
  });

  const { props } = useViewParamsProps(viewsService.currentView);

  const progressDisplayOption = computed(() => props.value.find((p) => p.key === goalConfig.edges.cachedCalculatedCurrent).progressDisplayOption);

  return {
    order,
    applyFilterOnFirstLevelOnly,
    showParents,
    queryFilter,
    gqlFilterObject,
    showCompareTo,
    compareTo,
    progressDisplayOption,
    cardWidth,
    searchTerm,
    selectedCycles,
    props,
  };
}
